import type { BookingDetailsData, TransferItem } from '../../domain/types'
import type { TransferCardsAlignment } from '../selectors/transferSelectors'
import type { Locale } from '@/intl/types'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from 'react-intl'
import { formatDate } from '@/scopes/account/common/formatters'
import { IconRow } from './IconRow'
import styles from './TransferCard.module.css'

const INSTRUCTION_KEYS = [
  'booking_details_transfers_instruction_step_1',
  'booking_details_transfers_instruction_step_2',
  'booking_details_transfers_instruction_step_3',
] as const

interface TransferCardProps {
  title: string
  noTransferTitle: string
  noTransferDescription: string
  transferType: BookingDetailsData['transferType']
  transfer: TransferItem | null
  transferDirection: 'outbound' | 'inbound'
  alignRows?: TransferCardsAlignment
}

export function TransferCard({
  title,
  noTransferTitle,
  noTransferDescription,
  transferType,
  transfer,
  transferDirection,
  alignRows,
}: TransferCardProps) {
  const { formatMessage, locale } = useIntl()
  const currentLocale = locale as Locale

  if (!transfer) {
    const emptyIcon = transferType === 'Private' ? 'CarFront' : 'BusFront'
    return (
      <div className={styles.emptyCard}>
        <Icon glyph={emptyIcon} size={32} className={styles.emptyIcon} />
        <div>
          <Typography as="h3" variant="display-i-regular">
            {noTransferTitle}
          </Typography>
          <Typography as="p" variant="body-s-regular-loose" className={styles.emptyDescription}>
            {noTransferDescription}
          </Typography>
        </div>
      </div>
    )
  }

  const hasInfo = Boolean(transfer.pickupLocation)
  const cardClassName = alignRows === 'full'
    ? styles.cardAligned
    : alignRows === 'header' ? styles.cardHeaderAligned : styles.card

  return (
    <div className={cardClassName}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <Typography as="h3" variant="display-i-regular">
            {title}
          </Typography>
          {transfer.isCancelled && (
            <Tag variant="danger-tinted">
              {formatMessage({ id: 'common_cancelled' })}
            </Tag>
          )}
        </div>
        <Typography as="p" variant="body-s-regular-tight" className={styles.date}>
          {formatDate(transfer.pickupDate, currentLocale)}
        </Typography>
      </div>
      <div className={styles.body}>
        {hasInfo
          ? (
              <div className={styles.content}>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <IconRow glyph="MapPin" description={formatMessage({ id: 'booking_details_transfers_pickup_location' })} />
                    <Typography as="p" variant="body-m-bold-tight" className={styles.fieldValue}>
                      {transfer.pickupLocation}
                    </Typography>
                  </div>

                  <div className={styles.fieldRight}>
                    <IconRow glyph="Clock" description={formatMessage({ id: 'booking_details_transfers_pickup_time' })} />
                    <Typography as="p" variant="body-m-bold-tight" className={styles.fieldValue}>
                      {transfer.pickupTime}
                    </Typography>
                  </div>
                </div>
              </div>
            )
          : transferDirection === 'inbound'
            ? (
                <div className={styles.content}>
                  <div className={styles.row}>
                    <div className={styles.field}>
                      <IconRow glyph="MapPin" description={formatMessage({ id: 'booking_details_transfers_pickup_location_and_time' })} />
                      <Typography as="p" variant="body-m-regular-tight" className={styles.fieldValue}>
                        {formatMessage({ id: 'booking_details_transfers_inbound_no_info_description' })}
                      </Typography>
                    </div>
                  </div>
                </div>
              )
            : (
                <ol className={styles.steps}>
                  {INSTRUCTION_KEYS.map((id, index) => (
                    <li key={id} className={styles.step}>
                      <span className={styles.stepNumber} aria-hidden="true">
                        <Typography as="span" variant="body-s-bold-tight">{index + 1}</Typography>
                      </span>
                      <Typography as="span" variant="body-m-regular-tight">
                        {formatMessage({ id })}
                      </Typography>
                    </li>
                  ))}
                </ol>
              )}
        {(hasInfo || transferDirection === 'inbound') && (
          <div className={styles.remark}>
            <IconRow glyph="InformationInCircle" description={formatMessage({ id: transferDirection === 'outbound' ? 'booking_details_transfers_outbound_disclaimer' : 'booking_details_transfers_disclaimer' })} />
          </div>
        )}
      </div>
    </div>
  )
}
